import { useEmbarcacaoClasse } from "@/modules/embarcacoes";
import type { EmbarcacaoClasse, EmbarcacaoClasseField } from "@/modules/embarcacoes";
import type { EmptyStateContent } from "@/screens/control-panel/components/empty-state";
import {
  DetailField,
  DetailSection,
  DetailSectionFields,
  DetailSectionState,
} from "../detail-section";
import { formatDetailDate } from "../embarcacao-detail-format";
import type { EmbarcacaoDetailSectionProps } from "../embarcacao-detail-section";

export const CLASSE_ID = "classe";
export const CLASSE_TITLE = "Classe";
export const CLASSE_LOADING_MESSAGE = "Carregando a Classe da embarcação...";

/** Padrão global de estado vazio do Hub para a seção sem informações (AC5). */
const CLASSE_EMPTY_STATE: EmptyStateContent = {
  title: "Nenhuma informação de Classe disponível",
  description:
    "Assim que houver informações de classe para esta embarcação, elas aparecerão nesta seção.",
};

const TEST_ID = "embarcacao-classe";

interface ClasseFieldDefinition {
  readonly key: EmbarcacaoClasseField;
  readonly label: string;
  /** Tratamento de apresentação do valor; sem ele o valor vai como recebido. */
  readonly format?: (value: string) => string;
  /** Campo que abre linha no grid, para reproduzir o pareamento do protótipo. */
  readonly startsRow?: boolean;
}

/**
 * Os 19 campos da seção, na ordem e nas colunas do protótipo (AC2). O grid os
 * distribui em duas colunas, preenchendo a linha da esquerda para a direita.
 *
 * O protótipo isola cada janela de vistoria: Última Especial e Última
 * Intermediária fecham a sua janela sozinhas na coluna da esquerda, e o mesmo
 * vale para Bottom Survey (To). Por isso Intermediária (From), Última Bottom
 * Survey e Thruster (To) abrem linha; nos demais pontos o preenchimento
 * corrido já reproduz as colunas desenhadas, par a par.
 *
 * As datas recebem apenas o formato de leitura do Hub — nenhum valor é
 * calculado ou derivado (AC3).
 */
export const CLASSE_FIELDS: ClasseFieldDefinition[] = [
  { key: "notacaoClasse", label: "Notação de Classe" },
  {
    key: "validadeNotacaoClasse",
    label: "Data de Validade da Notação de Classe",
    format: formatDetailDate,
  },
  { key: "classificadoraAtual", label: "Classificadora Atual" },
  { key: "ultimoReportClasse", label: "Último report de classe", format: formatDetailDate },
  { key: "anualFrom", label: "Anual (From)", format: formatDetailDate },
  { key: "anualTo", label: "Anual (To)", format: formatDetailDate },
  { key: "especialFrom", label: "Especial (From)", format: formatDetailDate },
  { key: "especialTo", label: "Especial (To)", format: formatDetailDate },
  { key: "ultimaEspecial", label: "Última Especial", format: formatDetailDate },
  {
    key: "intermediariaFrom",
    label: "Intermediária (From)",
    format: formatDetailDate,
    startsRow: true,
  },
  { key: "intermediariaTo", label: "Intermediária (To)", format: formatDetailDate },
  { key: "ultimaIntermediaria", label: "Última Intermediária", format: formatDetailDate },
  {
    key: "ultimaBottom",
    label: "Última Bottom Survey",
    format: formatDetailDate,
    startsRow: true,
  },
  {
    key: "condicaoUltimaBottom",
    label: "Condição da Última Bottom Survey",
    format: formatDetailDate,
  },
  { key: "bottomTo", label: "Bottom Survey (To)", format: formatDetailDate },
  { key: "thrusterTo", label: "Thruster (To)", format: formatDetailDate, startsRow: true },
  { key: "propulsaoTo", label: "Propulsão (To)", format: formatDetailDate },
  // O AC2 e a tela de edição do protótipo pedem o último FMEA antes do seu
  // vencimento; só a tela de visualização os desenha na ordem inversa.
  { key: "ultimoFmea", label: "Último FMEA", format: formatDetailDate },
  { key: "vencimentoFmea", label: "Vencimento FMEA", format: formatDetailDate },
];

/** Valor do campo pronto para a tela; `null` segue como campo sem preenchimento. */
function fieldValue(
  classe: EmbarcacaoClasse,
  { key, format }: ClasseFieldDefinition,
): string | null {
  const value = classe[key];
  if (value === null) return null;

  return format ? format(value) : value;
}

/**
 * Seção Classe do detalhamento da embarcação (US-1793).
 *
 * Exclusivamente de leitura (RN003): nenhum botão ou campo de cadastro, edição
 * ou exclusão. Os valores são apresentados como recebidos da fonte, com os
 * únicos tratamentos previstos — data no padrão do Hub e "Campo sem
 * preenchimento" para valor ausente (AC3).
 */
export function ClasseSection({ embarcacaoId, onReauthenticate }: EmbarcacaoDetailSectionProps) {
  const { status, classe, errorMessage, reload } = useEmbarcacaoClasse(embarcacaoId);

  return (
    <DetailSection
      id={CLASSE_ID}
      title={CLASSE_TITLE}
      // Só há data de atualização a exibir quando a seção tem dados.
      ultimaAtualizacao={classe ? classe.ultimaAtualizacao : undefined}
      testId={TEST_ID}
    >
      {classe ? (
        <DetailSectionFields testId={`${TEST_ID}-fields`}>
          {CLASSE_FIELDS.map((field) => (
            <DetailField
              key={field.key}
              label={field.label}
              value={fieldValue(classe, field)}
              startsRow={field.startsRow}
              testId={`${TEST_ID}-${field.key}`}
            />
          ))}
        </DetailSectionFields>
      ) : (
        <DetailSectionState
          // `success` sempre traz os campos: aqui só chegam carregamento e exceções.
          status={status === "success" ? "error" : status}
          loadingMessage={CLASSE_LOADING_MESSAGE}
          errorMessage={errorMessage}
          emptyContent={CLASSE_EMPTY_STATE}
          onReauthenticate={onReauthenticate}
          onRetry={reload}
          testId={TEST_ID}
        />
      )}
    </DetailSection>
  );
}
